import {
  Box,
  Button,
  VStack,
  useToast,
  Text,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Code,
  Alert,
  AlertIcon,
  OrderedList,
  ListItem,
  HStack,
} from '@chakra-ui/react';
import { useState } from 'react';
import { SESClient, SendEmailCommand, VerifyEmailIdentityCommand } from "@aws-sdk/client-ses";

const EmailSESTest = () => {
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState(null);
  const toast = useToast();

  const verifyEmails = async () => {
    setVerifying(true);
    try {
      const sesClient = new SESClient({
        credentials: {
          accessKeyId: import.meta.env.VITE_AWS_ACCESS_KEY_ID,
          secretAccessKey: import.meta.env.VITE_AWS_SECRET_ACCESS_KEY,
        },
        region: import.meta.env.VITE_AWS_REGION
      });

      // Verify sender email
      const verifyFromCommand = new VerifyEmailIdentityCommand({
        EmailAddress: import.meta.env.VITE_AWS_SES_FROM_EMAIL
      });
      await sesClient.send(verifyFromCommand);

      // Verify recipient email
      const verifyToCommand = new VerifyEmailIdentityCommand({
        EmailAddress: 'mike@mkthomas.com'
      });
      await sesClient.send(verifyToCommand);

      toast({
        title: 'Verification Emails Sent',
        description: 'Please check both email addresses for verification links.',
        status: 'success',
        duration: 5000,
      });
    } catch (error) {
      console.error('Verification error:', error);
      toast({
        title: 'Error',
        description: `Failed to send verification emails: ${error.message}`,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setVerifying(false);
    }
  };

  const sendTestEmail = async () => {
    setLoading(true);
    setResult(null);

    try {
      // Configure SES Client
      const sesClient = new SESClient({
        credentials: {
          accessKeyId: import.meta.env.VITE_AWS_ACCESS_KEY_ID,
          secretAccessKey: import.meta.env.VITE_AWS_SECRET_ACCESS_KEY,
        },
        region: import.meta.env.VITE_AWS_REGION
      });

      // Create the test email
      const params = {
        Destination: {
          ToAddresses: ['mike@mkthomas.com']
        },
        Message: {
          Body: {
            Html: {
              Charset: 'UTF-8',
              Data: `
                <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                  <h2>AWS SES Test Email</h2>
                  <p>This is a test email sent from the Rotary Messaging app using AWS SES.</p>
                  <p>Configuration used:</p>
                  <ul>
                    <li>Region: ${import.meta.env.VITE_AWS_REGION}</li>
                    <li>From: ${import.meta.env.VITE_AWS_SES_FROM_EMAIL}</li>
                    <li>To: mike@mkthomas.com</li>
                    <li>Time: ${new Date().toLocaleString()}</li>
                  </ul>
                  <p>If you received this email, AWS SES is configured correctly!</p>
                </div>
              `
            },
            Text: {
              Charset: 'UTF-8',
              Data: `AWS SES Test Email\n\nThis is a test email sent from the Rotary Messaging app using AWS SES.\n\nIf you received this email, AWS SES is configured correctly!`
            }
          },
          Subject: {
            Charset: 'UTF-8',
            Data: 'AWS SES Test Email'
          }
        },
        Source: import.meta.env.VITE_AWS_SES_FROM_EMAIL
      };

      // Send the email
      const command = new SendEmailCommand(params);
      const response = await sesClient.send(command);

      setResult({
        success: true,
        messageId: response.MessageId,
        timestamp: new Date().toISOString()
      });

      toast({
        title: 'Email Sent',
        description: 'Test email sent successfully. Check your inbox.',
        status: 'success',
        duration: 5000,
      });
    } catch (error) {
      console.error('SES error:', error);
      setResult({
        success: false,
        error: error.message,
        timestamp: new Date().toISOString()
      });

      toast({
        title: 'Error',
        description: `Failed to send email: ${error.message}`,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxW="800px" mx="auto" py={8}>
      <Card>
        <CardHeader>
          <Heading size="lg">AWS SES Test</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={6} align="stretch">
            <Alert status="warning">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">AWS SES Sandbox Mode</Text>
                <Text>
                  Your AWS SES account is in sandbox mode. Both sender ({import.meta.env.VITE_AWS_SES_FROM_EMAIL}) 
                  and recipient (mike@mkthomas.com) email addresses must be verified in the AWS SES console before sending emails.
                </Text>
              </Box>
            </Alert>

            <Box>
              <Text fontWeight="bold" mb={2}>Required Steps:</Text>
              <OrderedList spacing={2} mb={4}>
                <ListItem>
                  Verify sender email ({import.meta.env.VITE_AWS_SES_FROM_EMAIL})
                </ListItem>
                <ListItem>
                  Verify recipient email (mike@mkthomas.com)
                </ListItem>
                <ListItem>
                  Wait a few minutes after verification
                </ListItem>
              </OrderedList>
              
              <Button
                colorScheme="yellow"
                onClick={verifyEmails}
                isLoading={verifying}
                loadingText="Sending verification emails..."
                mb={4}
              >
                Send Verification Emails
              </Button>
            </Box>

            <Box>
              <Text fontWeight="bold" mb={2}>Current Configuration:</Text>
              <Code p={4} borderRadius="md" display="block" whiteSpace="pre">
{`Region: ${import.meta.env.VITE_AWS_REGION}
From: ${import.meta.env.VITE_AWS_SES_FROM_EMAIL}
To: mike@mkthomas.com`}
              </Code>
            </Box>

            {result && (
              <Alert status={result.success ? 'success' : 'error'}>
                <AlertIcon />
                {result.success ? (
                  <Text>Email sent successfully! Message ID: {result.messageId}</Text>
                ) : (
                  <Text>Failed to send email: {result.error}</Text>
                )}
              </Alert>
            )}

            <Button
              colorScheme="blue"
              onClick={sendTestEmail}
              isLoading={loading}
              loadingText="Sending..."
            >
              Send Test Email
            </Button>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};

export default EmailSESTest;
